// 访客记录请求与首次访问停留时间上报
// 接口：POST /ip/save
// 入参示例：{ visitPage: 'string', stopTime: 100 }

// 上报访客记录（优先使用 sendBeacon，回退到 fetch）
export function postVisitRecord(payload) {
  // 统一走 /api 前缀，配合 devServer.proxy 的 pathRewrite 将在后端收到 /ip/save
  const url = '/api/ip/save';
  try {
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const blob = new Blob([JSON.stringify(payload)], {
        type: 'application/json',
      });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch (e) {
    // 忽略 sendBeacon 异常，继续尝试 fetch
    // eslint-disable-next-line no-console
    console.warn('sendBeacon 失败，回退至 fetch', e);
  }
  try {
    // keepalive 在页面卸载时也可尝试发送
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // @ts-ignore: 部分环境可能不支持 keepalive
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('fetch 上报失败', e);
  }
}

// 初始化路由停留时间上报：在每次路由切换时上报上一页面的停留时间
export function initVisitTracker(router) {
  let currentStartTime = Date.now();
  let currentPage =
    (router && router.currentRoute && router.currentRoute.fullPath) ||
    (typeof window !== 'undefined'
      ? window.location.pathname + window.location.search + window.location.hash
      : '/');

  // 在每次完成导航后，上报离开的页面（from）的停留时间
  if (router && typeof router.afterEach === 'function') {
    router.afterEach((to, from) => {
      try {
        // 计算离开页面（from）的停留时间
        const stopTimeSec = Math.max(
          0,
          Math.round((Date.now() - currentStartTime) / 1000)
        );
        const visitPage = (from && from.fullPath) || currentPage;
        postVisitRecord({ visitPage, stopTime: stopTimeSec });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('路由切换上报失败', e);
      }
      // 更新当前页面与开始时间为新页面（to）
      currentPage = (to && to.fullPath) || currentPage;
      currentStartTime = Date.now();
    });
  }

  // 页面关闭或进入后台：兜底上报当前页面的停留时间
  if (typeof window !== 'undefined') {
    const submitCurrent = () => {
      try {
        const stopTimeSec = Math.max(
          0,
          Math.round((Date.now() - currentStartTime) / 1000)
        );
        postVisitRecord({ visitPage: currentPage, stopTime: stopTimeSec });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('页面卸载上报失败', e);
      }
    };

    // Safari 推荐使用 pagehide；其他浏览器 visibilitychange 也有效
    window.addEventListener('pagehide', submitCurrent);
    window.addEventListener('beforeunload', submitCurrent);
    document.addEventListener('visibilitychange', () => {
      try {
        if (document.visibilityState === 'hidden') submitCurrent();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('visibilityState 读取失败', e);
      }
    });
  }
}

// 新闻接口：列表与详情
let __newsCache = null;

function normalizeImageUrl(u) {
  if (!u) return '';
  try {
    const s = String(u).replace(/`/g, '').trim();
    // 修复常见域名拼写错误与协议错误
    const fixed = s
      .replace(/,com/gi, '.com')
      .replace(/httos:\/\//i, 'https://')
      .replace(/http:\/\/(?=[^/])/i, 'http://');
    // 仅取第一个 URL 片段
    const m = fixed.match(/https?:\/\/[^\s'"]+/i);
    const url = m ? m[0] : fixed;
    return url.replace(/\s/g, '%20');
  } catch (e) {
    return u;
  }
}

function mapTypeKey(t) {
  switch (t) {
    case '公司动态':
      return 'company';
    case '行业动态':
      return 'industry';
    case '产品动态':
      return 'product';
    case '展会动态':
      return 'expo';
    default:
      return 'company';
  }
}

export async function fetchNewsList() {
  try {
    const res = await fetch('/api/news/selectAll', { method: 'GET' });
    const json = await res.json();
    if (json && json.code === 200 && Array.isArray(json.data)) {
      const list = json.data.map((it) => ({
        id: it.id,
        title: it.title || '',
        typeLabel: it.type || '',
        typeKey: mapTypeKey(it.type),
        // photoUrl: normalizeImageUrl(it.photoUrl),
        photoUrl: it.photoUrl,
        author: it.author || '',
        profile: it.profile || '',
        content: it.content || '',
        pageView: it.pageView == null ? 0 : it.pageView,
        state: it.state,
        createTime: it.createTime || '',
      }));
      __newsCache = list;
      return list;
    }
    throw new Error('Invalid news response');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('新闻列表获取失败', e);
    throw e;
  }
}

export async function fetchNewsById(id) {
  const numId = String(id);
  try {
    if (__newsCache && Array.isArray(__newsCache)) {
      const found = __newsCache.find((it) => String(it.id) === numId);
      if (found) return found;
    }
    const list = await fetchNewsList();
    return list.find((it) => String(it.id) === numId) || null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('新闻详情获取失败', e);
    return null;
  }
}

// SEO 接口：按路由分配 title/keyword/describe
let __seoCache = null;

function normalizePath(path) {
  try {
    const p = String(path || '/');
    if (p === '/') return '/';
    return p.replace(/\/$/, '');
  } catch (e) {
    return '/';
  }
}

export async function fetchSeoTable() {
  try {
    const res = await fetch('/api/seoTable/selectAll', { method: 'GET' });
    const json = await res.json();
    if (json && json.code === 200 && Array.isArray(json.data)) {
      const list = json.data.map((it) => ({
        id: it.id,
        path: normalizePath(it.path),
        title: it.title || '',
        keyword: it.keyword || '',
        describe: it.describe || '',
        createTime: it.createTime || '',
      }));
      __seoCache = list;
      return list;
    }
    throw new Error('Invalid SEO response');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('SEO 列表获取失败', e);
    throw e;
  }
}

// 根据路由 path 匹配最合适的 SEO 数据（优先精确匹配，其次最长前缀匹配）
export async function findSeoForPath(path) {
  const cleaned = normalizePath(path);
  try {
    if (!__seoCache) await fetchSeoTable();
    const list = Array.isArray(__seoCache) ? __seoCache : [];
    // 精确匹配
    const exact = list.find((it) => it.path === cleaned);
    if (exact) return exact;
    // 前缀匹配：选择最长匹配项，例如 /about/news/123 命中 /about/news
    const candidates = list
      .filter((it) => cleaned.startsWith(it.path))
      .sort((a, b) => b.path.length - a.path.length);
    if (candidates.length) return candidates[0];
    return null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('SEO 匹配失败', e);
    return null;
  }
}

// 软件中心接口：下载项列表
function __stripLower(v) {
  try {
    return String(v || '')
      .toLowerCase()
      .replace(/[\s-]/g, '');
  } catch (e) {
    return '';
  }
}

// 根据名称/类型/系统推断软件中心的 key，用于页面内容切换
function inferSoftKey(name, type, system) {
  const n = __stripLower(name);
  const t = __stripLower(type);
  const s = __stripLower(system);

  const hasAppSignal =
    /(app|安卓|android|ios|移动端)/.test(n) ||
    /(app|android|ios)/.test(t) ||
    /(android|ios)/.test(s);
  const hasLightMaker = /(lightmaker)/.test(n) || /(lightmaker)/.test(t);
  const hasLearning =
    /(zetok|中泽|学习平台|learningplatform|learning)/.test(n) ||
    /(learning|platform)/.test(t);

  // 优先判断移动端 App
  if (hasAppSignal) {
    if (hasLightMaker) return 'lightmaker-app';
    if (hasLearning) return 'zz-learning-app';
  }

  // 桌面端 Light Maker 客户端
  if (hasLightMaker && !hasAppSignal) return 'goodlaser-lightmaker';

  // 桌面端学习平台客户端
  if (hasLearning && !/(app)/.test(n)) return 'zz-learning-platform';

  // 兜底：根据类型再尝试一次
  if (/(lightmaker)/.test(t)) return 'goodlaser-lightmaker';
  if (/(learning)/.test(t) && /(app)/.test(t)) return 'zz-learning-app';
  if (/(learning|platform)/.test(t)) return 'zz-learning-platform';

  return null;
}

export async function fetchSoftTableList() {
  try {
    const res = await fetch('/api/softTable/selectAll', { method: 'GET' });
    const json = await res.json();
    if (json && json.code === 200 && Array.isArray(json.data)) {
      const list = json.data.map((it) => {
        const name = it && it.name ? String(it.name).trim() : '';
        // const photoUrl = normalizeImageUrl(it && it.photoUrl);
        const photoUrl = it && it.photoUrl
        const url = normalizeImageUrl(it && it.url);
        const key = inferSoftKey(name, it && it.type, it && it.system);
        return {
          id: it.id,
          name,
          type: it.type || '',
          system: it.system || '',
          version: it.version || '',
          num: it.num == null ? 0 : it.num,
          createTime: it.createTime || '',
          photoUrl,
          url,
          key,
        };
      });
      return list;
    }
    throw new Error('Invalid softTable response');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('软件下载列表获取失败', e);
    throw e;
  }
}

// 售后服务接口：教育工具包列表（优先下载视频，否则下载文件）
export async function fetchEducationalKitList() {
  try {
    const res = await fetch('/api/educationalKit/selectAll', { method: 'GET' });
    const json = await res.json();
    if (json && json.code === 200 && Array.isArray(json.data)) {
      const list = json.data.map((it) => {
        const name = it && it.name ? String(it.name).trim() : '';
        // const image = normalizeImageUrl(it && it.imageUrl);
        const image = it && it.imageUrl;
        // const video = normalizeImageUrl(it && it.videoUrl);
        const video = it && it.videoUrl;
        // const file = normalizeImageUrl(it && it.fileUrl);
        const file = it && it.fileUrl;
        const sortOrderNum = Number(it && it.sortOrder);
        return {
          id: it.id,
          name,
          image,
          video,
          file,
          sortOrder: Number.isFinite(sortOrderNum) ? sortOrderNum : 0,
          createTime: it && it.createTime ? String(it.createTime) : '',
          // 提示文案：若有视频则“下载视频”，否则“下载教程”
          // btnText: video ? '下载视频' : '下载教程',
          btnText: '下载文件',
        };
      });
      // 按 sortOrder 升序
      list.sort((a, b) => a.sortOrder - b.sortOrder);
      return list;
    }
    throw new Error('Invalid educational kit response');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('教育工具包列表获取失败', e);
    throw e;
  }
}
